import { useEffect, useState } from 'react';
import { IconPlus, IconShield } from '../../components/icons';
import {
  Badge,
  Card,
  EmptyState,
  ErrorMessage,
  Field,
  Loading,
  Modal,
  ModuleHeader,
  useAsync,
  useToast,
} from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { PermissionMatrix, type Grant } from './PermissionMatrix';

const KEY_OK = /^[a-z0-9_.-]{2,60}$/;

/** „Objektleitung Nord“ → „objektleitung_nord“. */
const toKey = (text: string) =>
  text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

export function RolesPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [selected, setSelected] = useState<number | null>(null);
  const [createModal, setCreateModal] = useState(false);

  const roles = useAsync(() => api.get('/api/roles'), []);
  const catalog = useAsync(() => api.get('/api/roles/catalog/permissions'), []);

  useEffect(() => {
    if (!selected && roles.data?.length) setSelected(roles.data[0].id);
  }, [roles.data, selected]);

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconShield width={22} height={22} />}
        title="Rollen & Berechtigungen"
        subtitle={
          <>
            Rollen bündeln Rechte je Modul. Jedes Recht kann erlaubt oder ausdrücklich verweigert werden – und
            erhält einen Scope, der die sichtbaren Datensätze bestimmt.
          </>
        }
        pill="Berechtigungen"
        tone="danger"
        actions={
          can('admin.roles.manage') && (
            <button className="btn primary" onClick={() => setCreateModal(true)}>
              <IconPlus width={15} height={15} /> Rolle anlegen
            </button>
          )
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '300px minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
        <Card title="Rollen" noBody>
          {roles.loading ? (
            <Loading />
          ) : (
            <div style={{ padding: 8 }}>
              {(roles.data ?? []).map((role: any) => (
                <button
                  key={role.id}
                  className={`nav-item ${selected === role.id ? 'active' : ''}`}
                  style={{ width: '100%', border: 'none', background: selected === role.id ? undefined : 'transparent', cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => setSelected(role.id)}
                >
                  <span
                    className="icon"
                    style={{ width: 8, height: 8, borderRadius: '50%', background: role.color ?? 'var(--border-strong)' }}
                  />
                  <span style={{ flex: 1 }}>
                    {role.name}
                    <div className="small subtle">
                      {role.permission_count} Rechte · {role.user_count + role.group_count} Zuweisungen
                    </div>
                  </span>
                  {role.is_system ? <Badge>System</Badge> : null}
                </button>
              ))}
            </div>
          )}
        </Card>

        {selected ? (
          <RoleEditor
            roleId={selected}
            catalog={catalog.data}
            onSaved={() => {
              roles.reload();
              notify('Rolle gespeichert.', 'success');
            }}
            onDeleted={() => {
              setSelected(null);
              roles.reload();
              notify('Rolle gelöscht.', 'success');
            }}
          />
        ) : (
          <Card>
            <EmptyState title="Keine Rolle ausgewählt" />
          </Card>
        )}
      </div>

      {createModal && (
        <CreateRoleModal
          onClose={() => setCreateModal(false)}
          onSaved={(id) => {
            setCreateModal(false);
            roles.reload();
            setSelected(id);
            notify('Rolle angelegt.', 'success');
          }}
        />
      )}
    </div>
  );
}

function RoleEditor({
  roleId,
  catalog,
  onSaved,
  onDeleted,
}: {
  roleId: number;
  catalog: any;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { can } = useAuth();
  const role = useAsync(() => api.get(`/api/roles/${roleId}`), [roleId]);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [duplicate, setDuplicate] = useState(false);

  useEffect(() => {
    if (role.data) {
      setGrants(
        (role.data.permissions ?? []).map((row: any) => ({
          permissionKey: row.permission_key,
          effect: row.effect,
          scope: row.scope,
        })),
      );
      setName(role.data.name);
      setDescription(role.data.description ?? '');
    }
  }, [role.data]);

  if (role.loading) return <Card><Loading /></Card>;

  const mayEdit = can('admin.roles.manage');

  return (
    <Card
      title={role.data?.name}
      subtitle={`Schlüssel: ${role.data?.key} · ${grants.length} Rechte gesetzt`}
      actions={
        mayEdit ? (
          <>
            <button className="btn sm" onClick={() => setDuplicate(true)}>
              Duplizieren
            </button>
            {!role.data?.is_system && (
              <button
                className="btn sm danger"
                onClick={async () => {
                  const users = role.data.users?.length ?? 0;
                  const groups = role.data.groups?.length ?? 0;
                  const folgen = users + groups > 0 ? `\n\nSie ist ${users} Benutzer(n) und ${groups} Gruppe(n) zugewiesen – diese Zuweisungen entfallen und die Betroffenen verlieren die Rechte der Rolle.` : '';
                  if (!window.confirm(`Rolle "${role.data.name}" wirklich löschen?${folgen}`)) return;
                  await api.delete(`/api/roles/${roleId}`);
                  onDeleted();
                }}
              >
                Löschen
              </button>
            )}
            <button
              className="btn primary sm"
              onClick={async () => {
                try {
                  // Systemrollen ändert der Server nur mit dem eigenen Passwort.
                  const save = role.data?.is_system ? api.patchConfirmed : api.patch;
                  await save(`/api/roles/${roleId}`, { name, description, permissions: grants });
                  setError(null);
                  onSaved();
                  role.reload();
                } catch (caught) {
                  if (!(caught instanceof ApiError && caught.code === 'cancelled')) setError(caught);
                }
              }}
            >
              Speichern
            </button>
          </>
        ) : null
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />

        <div className="form-grid">
          <Field label="Bezeichnung">
            <input value={name} disabled={!mayEdit} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Beschreibung">
            <input value={description} disabled={!mayEdit} onChange={(event) => setDescription(event.target.value)} />
          </Field>
        </div>

        {(role.data?.users?.length > 0 || role.data?.groups?.length > 0) && (
          <div className="alert info small">
            Zugewiesen an {role.data.users.length} Benutzer und {role.data.groups.length} Gruppe(n). Änderungen
            wirken sich unmittelbar auf alle aus.
          </div>
        )}

        <PermissionMatrix
          catalog={catalog?.permissions ?? []}
          modules={catalog?.modules ?? []}
          grants={grants}
          onChange={setGrants}
          readOnly={!mayEdit}
        />
      </div>

      {duplicate && (
        <DuplicateModal
          roleId={roleId}
          baseName={role.data?.name ?? ''}
          onClose={() => setDuplicate(false)}
          onSaved={() => {
            setDuplicate(false);
            onSaved();
          }}
        />
      )}
    </Card>
  );
}

function CreateRoleModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id: number) => void }) {
  const [form, setForm] = useState({ key: '', name: '', description: '' });
  // Solange der Schlüssel nicht von Hand angefasst wurde, folgt er der Bezeichnung.
  const [keyTouched, setKeyTouched] = useState(false);
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title="Rolle anlegen"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!KEY_OK.test(form.key) || form.name.trim().length < 2}
            onClick={async () => {
              try {
                const result = await api.post('/api/roles', { ...form, permissions: [] });
                onSaved(result.id);
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Anlegen
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <Field label="Bezeichnung" required>
          <input
            value={form.name}
            autoFocus
            onChange={(event) =>
              setForm({
                ...form,
                name: event.target.value,
                key: keyTouched ? form.key : toKey(event.target.value),
              })
            }
          />
        </Field>
        <Field label="Schlüssel" required hint="Technischer Bezeichner aus Kleinbuchstaben, Ziffern, . _ - (2–60 Zeichen), z. B. objektleitung.">
          <input
            value={form.key}
            onChange={(event) => {
              setKeyTouched(true);
              setForm({ ...form, key: event.target.value });
            }}
          />
        </Field>
        <Field label="Beschreibung">
          <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}

function DuplicateModal({
  roleId,
  baseName,
  onClose,
  onSaved,
}: {
  roleId: number;
  baseName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(`${baseName} (Kopie)`);
  const [key, setKey] = useState(() => toKey(`${baseName} kopie`));
  const [error, setError] = useState<unknown>(null);

  return (
    <Modal
      title="Rolle duplizieren"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn primary"
            disabled={!KEY_OK.test(key) || name.trim().length < 2}
            onClick={async () => {
              try {
                await api.post(`/api/roles/${roleId}/duplicate`, { key, name });
                onSaved();
              } catch (caught) {
                setError(caught);
              }
            }}
          >
            Duplizieren
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <p className="small muted">Die vollständigen Berechtigungen werden übernommen und können anschließend angepasst werden.</p>
        <Field label="Bezeichnung" required>
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Schlüssel" required hint="Kleinbuchstaben, Ziffern, . _ - (2–60 Zeichen).">
          <input value={key} onChange={(event) => setKey(event.target.value)} placeholder="z. B. disponent_nord" />
        </Field>
      </div>
    </Modal>
  );
}
